import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Form,
} from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { vacationApplicationsApiService } from '../../services/apiServices';
import { VacationApplicationResponse, VacationApplicationType } from '../../api-client';
import {
  getVacationApplicationStatusLabel,
  getVacationApplicationStatusBadgeVariant,
  getVacationApplicationTypeLabel,
  getVacationApplicationTypeBadgeVariant,
} from '../utils/statusConverters';
import VacationCreateForm from '../components/VacationCreateForm';
import { SmartPagination } from '../../components/SmartPagination';

const LIMIT = 10;

const MyVacationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState<VacationApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const typeFilter = searchParams.get('type') || '';

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * LIMIT;
      const response = await vacationApplicationsApiService.getMyApplications(
        (typeFilter as VacationApplicationType) || undefined,
        LIMIT,
        offset
      );
      setApplications(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleTypeFilterChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('type', value);
    } else {
      params.delete('type');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setCreateSuccess(true);
    loadApplications();
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  const handleUploadFile = async (file: File) => {
    const response = await vacationApplicationsApiService.uploadTempFile(file);
    return { fileUrl: response.data?.file_url || '' };
  };

  const handleGetTemplate = async (type: VacationApplicationType) => {
    const response = await vacationApplicationsApiService.getTemplate(type);
    return response.data?.file_url || '';
  };

  const handleCreateSubmit = async (
    type: VacationApplicationType,
    fileUrl: string,
    startDate: string,
    endDate: string,
    fileName?: string
  ) => {
    await vacationApplicationsApiService.createMyApplication(
      type,
      fileUrl,
      startDate,
      endDate,
      fileName
    );
    handleCreateSuccess();
  };

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Мои заявки на отпуск</h2>
            <Button variant="primary" onClick={() => setShowCreateForm(!showCreateForm)}>
              {showCreateForm ? 'Скрыть форму' : 'Новая заявка'}
            </Button>
          </div>

          {createSuccess && <Alert variant="success">Заявка успешно создана!</Alert>}

          {showCreateForm && (
            <Card className="mb-4">
              <Card.Header>Новая заявка на отпуск</Card.Header>
              <Card.Body>
                <VacationCreateForm
                  onSubmit={handleCreateSubmit}
                  onUploadFile={handleUploadFile}
                  onGetTemplate={handleGetTemplate}
                  onCancel={() => setShowCreateForm(false)}
                />
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={typeFilter}
                  onChange={(e) => handleTypeFilterChange(e.target.value)}
                >
                  <option value="">Все типы</option>
                  {Object.values(VacationApplicationType).map((t) => (
                    <option key={t} value={t}>
                      {getVacationApplicationTypeLabel(t)}
                    </option>
                  ))}
                </Form.Select>
                <Button variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                  Сбросить
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : error ? (
                <Alert variant="danger">{error}</Alert>
              ) : applications.length === 0 ? (
                <p className="text-muted text-center py-4">Заявки не найдены</p>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Тип</th>
                        <th>Статус</th>
                        <th>Дата создания</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td>
                            <Badge bg={getVacationApplicationTypeBadgeVariant(app.type)}>
                              {getVacationApplicationTypeLabel(app.type)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getVacationApplicationStatusBadgeVariant(app.status)}>
                              {getVacationApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleString()}</td>
                          <td>
                            <Link to={`/vacation/my/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <SmartPagination
                    currentPage={currentPage}
                    limit={LIMIT}
                    currentItemsCount={applications.length}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MyVacationPage;
